import { api } from './api'
import type { Permission } from '@/types'

// Interface para resposta da API de permissões
export interface PermissionsResponse {
  success: boolean
  data: Permission[]
  total?: number
  message?: string
}

export interface PermissionResponse {
  success: boolean
  data: Permission
  message?: string
}

export interface CreatePermissionData {
  nome: string
  descricao?: string
  categoria?: string
  ativo?: boolean
  user_id?: number
  permission_id?: string
}

export interface UpdatePermissionData extends Partial<CreatePermissionData> {}

// Serviço de gerenciamento de permissões
export const permissionsService = {
  // Listar todas as permissões
  async getPermissions(): Promise<PermissionsResponse> {
    try {
      const response = await api.get('/permissoes')

      if (response.data && (response.data.success || response.data.sucess)) {
        const permissions = response.data.data || response.data.permissions || []
        
        return {
          success: true,
          data: permissions,
          total: permissions.length,
          message: 'Permissões carregadas da API'
        }
      }

      console.warn('⚠️ API não retornou success=true')
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Erro ao carregar permissões da API'
      }
    } catch (error) {
      console.error('❌ Erro ao buscar permissões da API:', error)
      
      // Fallback para permissões padrão
      const defaultPermissions = this.getDefaultPermissions()
      return {
        success: true,
        data: defaultPermissions,
        total: defaultPermissions.length,
        message: 'Permissões carregadas (fallback)'
      }
    }
  },

  // Buscar permissão por ID
  async getPermissionById(id: number): Promise<PermissionResponse> {
    try {
      const response = await api.get(`/permissoes/${id}`)

      if (response.data && (response.data.success || response.data.sucess)) {
        const permission = response.data.data || response.data.permission
        
        return {
          success: true,
          data: permission,
          message: 'Permissão carregada da API'
        }
      }

      return {
        success: false,
        data: {} as Permission,
        message: 'Permissão não encontrada'
      }
    } catch (error) {
      console.error('❌ Erro ao buscar permissão:', error)
      return {
        success: false,
        data: {} as Permission,
        message: 'Erro ao buscar permissão'
      }
    }
  },

  // Buscar permissões por usuário
  async getPermissionsByUser(userId: number): Promise<PermissionsResponse> {
    try {
      const response = await api.get(`/permissoes/user/${userId}`)

      if (response.data && (response.data.success || response.data.sucess)) {
        const permissions = response.data.data || response.data.permissions || []
        
        return {
          success: true,
          data: permissions,
          total: permissions.length,
          message: 'Permissões do usuário carregadas da API'
        }
      }

      return {
        success: false,
        data: [],
        total: 0,
        message: 'Erro ao carregar permissões do usuário'
      }
    } catch (error) {
      console.error('❌ Erro ao buscar permissões do usuário:', error)
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Erro ao buscar permissões do usuário'
      }
    }
  },

  // Contar permissões
  async getPermissionsCount(): Promise<{ success: boolean; count: number; message?: string }> {
    try {
      const response = await api.get('/permissoes/count')
      
      if (response.data && (response.data.success || response.data.sucess)) {
        return {
          success: true,
          count: response.data.count || response.data.total || 0,
          message: 'Contagem carregada da API'
        }
      }

      return {
        success: false,
        count: 0,
        message: 'Erro ao carregar contagem'
      }
    } catch (error) {
      console.error('❌ Erro ao contar permissões:', error)
      return {
        success: false,
        count: 0,
        message: 'Erro ao contar permissões'
      }
    }
  },

  // Criar permissão
  async createPermission(permissionData: CreatePermissionData): Promise<PermissionResponse> {
    try {
      const response = await api.post('/permissoes', permissionData)

      if (response.data && (response.data.success || response.data.sucess)) {
        const permission = response.data.data || response.data.permission
        
        return {
          success: true,
          data: permission,
          message: 'Permissão criada com sucesso'
        }
      }

      return {
        success: false,
        data: {} as Permission,
        message: 'Erro ao criar permissão'
      }
    } catch (error) {
      console.error('❌ Erro ao criar permissão:', error)
      return {
        success: false,
        data: {} as Permission,
        message: 'Erro ao criar permissão'
      }
    }
  },

  // Atualizar permissão
  async updatePermission(id: number, permissionData: UpdatePermissionData): Promise<PermissionResponse> {
    try {
      const response = await api.put(`/permissoes/${id}`, permissionData)

      if (response.data && (response.data.success || response.data.sucess)) {
        const permission = response.data.data || response.data.permission
        
        return {
          success: true,
          data: permission,
          message: 'Permissão atualizada com sucesso'
        }
      }

      return {
        success: false,
        data: {} as Permission,
        message: 'Erro ao atualizar permissão'
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar permissão:', error)
      return {
        success: false,
        data: {} as Permission,
        message: 'Erro ao atualizar permissão'
      }
    }
  },

  // Deletar permissão
  async deletePermission(id: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete(`/permissoes/${id}`)

      if (response.data && (response.data.success || response.data.sucess)) {
        return {
          success: true,
          message: 'Permissão deletada com sucesso'
        }
      }

      return {
        success: false,
        message: 'Erro ao deletar permissão'
      }
    } catch (error) {
      console.error('❌ Erro ao deletar permissão:', error)
      return {
        success: false,
        message: 'Erro ao deletar permissão'
      }
    }
  },

  // Salvar permissões específicas para um usuário
  async saveUserPermissions(userId: number, roles: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      if (!userId || !roles || roles.length === 0) {
        return { success: false, message: 'Dados inválidos' }
      }
      
      const permissionsToSave = this.convertRolesToPermissions(roles)
      const existingPermissions = await this.getPermissionsByUser(userId)
      
      if (existingPermissions.success && existingPermissions.data.length > 0) {
        return await this.updateUserPermissions(userId, permissionsToSave, existingPermissions.data)
      }
      
      return await this.createUserPermissions(userId, permissionsToSave)
      
    } catch (error) {
      console.error('❌ Erro ao salvar permissões específicas:', error)
      return {
        success: false,
        message: 'Erro ao salvar permissões específicas'
      }
    }
  },

  // Criar novas permissões para um usuário
  async createUserPermissions(userId: number, permissions: Permission[]): Promise<{ success: boolean; message?: string }> {
    try {
      const results = []
        let successCount = 0
        let errorCount = 0
      for (const permission of permissions) {
        try {
          const createData = {
            descricao: permission.descricao || permission.description,
            id: permission.id,
            user_id: userId,
            status: true
          }
          
          const response = await api.post('/permissoes', createData)
          
          if (response.data && (response.data.success || response.data.sucess)) {
            successCount++
            results.push({ permission: permission.id, success: true })
          } else {
            errorCount++
            results.push({ permission: permission.id, success: false, error: 'API não retornou success' })
          }
        } catch (error) {
          errorCount++
          results.push({ permission: permission.id, success: false, error: (error as any).message })
        }
      }
      
      if (successCount > 0) {
        return {
          success: true,
          message: `Permissões criadas: ${successCount} sucesso(s), ${errorCount} erro(s)`
        }
      } else {
        return {
          success: false,
          message: `Falha ao criar permissões: ${errorCount} erro(s)`
        }
      }
    } catch (error) {
      console.error('❌ Erro ao criar permissões do usuário:', error)
      return {
        success: false,
        message: 'Erro ao criar permissões do usuário'
      }
    }
  },

  // Atualizar permissões existentes de um usuário
  async updateUserPermissions(userId: number, newPermissions: Permission[], existingPermissions: Permission[]): Promise<{ success: boolean; message?: string }> {
    try {
      
      // Identificar permissões para adicionar, atualizar e remover
      const newPermissionIds = new Set(newPermissions.map(p => p.id))
      const existingPermissionIds = new Set(existingPermissions.map(p => p.id))
      
      const toAdd = newPermissions.filter(p => !existingPermissionIds.has(p.id))
      const toUpdate = newPermissions.filter(p => existingPermissionIds.has(p.id))
      const toRemove = existingPermissions.filter(p => !newPermissionIds.has(p.id))
      
      
      const results = []
      let successCount = 0
      let errorCount = 0
      
      // Adicionar novas permissões
      for (const permission of toAdd) {
        try {
          const createData = {
            nome: permission.nome || permission.name,
            descricao: permission.descricao || permission.description,
            ativo: true,
          }
          
          const response = await api.post('/permissoes', createData)
          if (response.data && (response.data.success || response.data.sucess)) {
            successCount++
            results.push({ action: 'add', permission: permission.id, success: true })
          } else {
            errorCount++
            results.push({ action: 'add', permission: permission.id, success: false })
          }
        } catch (error) {
          errorCount++
          results.push({ action: 'add', permission: permission.id, success: false, error: (error as any).message })
        }
      }
      
      // Atualizar permissões existentes
      for (const permission of toUpdate) {
        try {
          const existingPermission = existingPermissions.find(p => p.id === permission.id)
          if (existingPermission) {
            const updateData = {
              nome: permission.nome || permission.name,
              descricao: permission.descricao || permission.description,
              ativo: true
            }
            
            // Usar o ID da permissão existente para atualizar
            const response = await api.put(`/permissoes/${existingPermission.id}`, updateData)
            if (response.data && (response.data.success || response.data.sucess)) {
              successCount++
              results.push({ action: 'update', permission: permission.id, success: true })
            } else {
              errorCount++
              results.push({ action: 'update', permission: permission.id, success: false })
            }
          }
        } catch (error) {
          errorCount++
          results.push({ action: 'update', permission: permission.id, success: false, error: (error as any).message })
        }
      }
      
      // Remover permissões que não estão mais na lista
      for (const permission of toRemove) {
        try {
          const response = await api.delete(`/permissoes/${permission.id}`)
          if (response.data && (response.data.success || response.data.sucess)) {
            successCount++
            results.push({ action: 'remove', permission: permission.id, success: true })
          } else {
            errorCount++
            results.push({ action: 'remove', permission: permission.id, success: false })
          }
        } catch (error) {
          errorCount++
          results.push({ action: 'remove', permission: permission.id, success: false, error: (error as any).message })
        }
      }
      
      
      if (successCount > 0) {
        return {
          success: true,
          message: `Permissões atualizadas: ${successCount} operação(ões) bem-sucedida(s), ${errorCount} erro(s)`
        }
      } else {
        return {
          success: false,
          message: `Falha ao atualizar permissões: ${errorCount} erro(s)`
        }
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar permissões do usuário:', error)
      return {
        success: false,
        message: 'Erro ao atualizar permissões do usuário'
      }
    }
  },

  // Obter categoria da permissão baseada no ID
  getCategoryFromPermission(permission: Permission): string {
    const id = permission.id.toLowerCase()
    
    if (id.includes('user')) return 'usuarios'
    if (id.includes('vistoria')) return 'vistorias'
    if (id.includes('calendario')) return 'calendarios'
    if (id.includes('cardapio')) return 'cardapios'
    if (id.includes('geolocation')) return 'geolocalizacao'
    if (id.includes('parametro')) return 'parametros'
    if (id.includes('relatorio')) return 'relatorios'
    if (id.includes('help')) return 'ajuda'
    if (id.includes('system')) return 'sistema'
    if (id.includes('dashboard')) return 'dashboard'
    
    return 'geral'
  },

  // Limpar todas as permissões de um usuário (método alternativo)
  async clearUserPermissions(userId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const existingPermissions = await this.getPermissionsByUser(userId)
      
      if (!existingPermissions.success || existingPermissions.data.length === 0) {
        return {
          success: true,
          message: 'Nenhuma permissão encontrada para limpar'
        }
      }
      
      let successCount = 0
      let errorCount = 0
      
      for (const permission of existingPermissions.data) {
        try {
          // Usar permissao_id em vez de id para deletar
          const permissionId = (permission as any).permissao_id || permission.id
          const response = await api.delete(`/permissoes/${permissionId}`)
          
          if (response.data && (response.data.success || response.data.sucess)) {
            successCount++
          } else {
            errorCount++
          }
        } catch (error) {
          errorCount++
        }
      }
      
      if (successCount > 0) {
        return {
          success: true,
          message: `Permissões removidas: ${successCount} sucesso(s), ${errorCount} erro(s)`
        }
      } else {
        return {
          success: false,
          message: `Falha ao remover permissões: ${errorCount} erro(s)`
        }
      }
    } catch (error) {
      console.error('❌ Erro ao limpar permissões do usuário:', error)
      return {
        success: false,
        message: 'Erro ao limpar permissões do usuário'
      }
    }
  },

  // Método simplificado: limpar e recriar todas as permissões
  async replaceUserPermissions(userId: number, roles: string[]): Promise<{ success: boolean; message?: string }> {
    try {
      // 1. Limpar permissões existentes
      const clearResult = await this.clearUserPermissions(userId)
      
      // 2. Criar novas permissões
      const permissionsToSave = this.convertRolesToPermissions(roles)
      const createResult = await this.createUserPermissions(userId, permissionsToSave)
      
      if (createResult.success) {
        return {
          success: true,
          message: `Permissões substituídas: ${createResult.message}`
        }
      } else {
        return {
          success: false,
          message: `Falha ao criar novas permissões: ${createResult.message}`
        }
      }
    } catch (error) {
      console.error('❌ Erro ao substituir permissões do usuário:', error)
      return {
        success: false,
        message: 'Erro ao substituir permissões do usuário'
      }
    }
  },

  // Converter roles em permissões específicas
  convertRolesToPermissions(roles: string[]): Permission[] {
    const permissions: Permission[] = []
    
    if (!roles || roles.length === 0) {
      return permissions
    }
    
    roles.forEach(role => {
      switch (role) {
        case 'administrador':
          // Administrador tem todas as permissões
          permissions.push(...this.getDefaultPermissions())
          break
        case 'vistoria':
          permissions.push(
            this.getDefaultPermissions().find(p => p.id === 'dashboard_view')!,
            this.getDefaultPermissions().find(p => p.id === 'vistoria_view')!,
            this.getDefaultPermissions().find(p => p.id === 'vistoria_create')!,
            this.getDefaultPermissions().find(p => p.id === 'vistoria_edit')!,
            this.getDefaultPermissions().find(p => p.id === 'help_view')!
          )
          break
        case 'calendario':
          permissions.push(
            this.getDefaultPermissions().find(p => p.id === 'dashboard_view')!,
            this.getDefaultPermissions().find(p => p.id === 'calendario_view')!,
            this.getDefaultPermissions().find(p => p.id === 'calendario_create')!,
            this.getDefaultPermissions().find(p => p.id === 'calendario_edit')!,
            this.getDefaultPermissions().find(p => p.id === 'help_view')!
          )
          break
        case 'cardapio':
          permissions.push(
            this.getDefaultPermissions().find(p => p.id === 'dashboard_view')!,
            this.getDefaultPermissions().find(p => p.id === 'cardapio_view')!,
            this.getDefaultPermissions().find(p => p.id === 'cardapio_create')!,
            this.getDefaultPermissions().find(p => p.id === 'cardapio_edit')!,
            this.getDefaultPermissions().find(p => p.id === 'help_view')!
          )
          break
        case 'usuario_basico':
          permissions.push(
            this.getDefaultPermissions().find(p => p.id === 'dashboard_view')!,
            this.getDefaultPermissions().find(p => p.id === 'help_view')!
          )
          break
      }
    })
    
    // Remover duplicatas
    const uniquePermissions = permissions.filter((permission, index, self) => 
      index === self.findIndex(p => p.id === permission.id)
    )
    
    return uniquePermissions
  },

  // Obter permissões padrão (fallback)
  getDefaultPermissions(): Permission[] {
    return [
      // Dashboard
      { 
        id: 'dashboard_view', 
        name: 'Visualizar Dashboard', 
        description: 'Permite visualizar o dashboard principal',
        resource: 'dashboard',
        action: 'view',
        nome: 'Visualizar Dashboard', 
        descricao: 'Permite visualizar o dashboard principal' 
      },
      
      // Usuários
      { 
        id: 'user_view', 
        name: 'Visualizar Usuários', 
        description: 'Permite visualizar a lista de usuários',
        resource: 'user',
        action: 'view',
        nome: 'Visualizar Usuários', 
        descricao: 'Permite visualizar a lista de usuários' 
      },
      { 
        id: 'user_create', 
        name: 'Criar Usuários', 
        description: 'Permite criar novos usuários',
        resource: 'user',
        action: 'create',
        nome: 'Criar Usuários', 
        descricao: 'Permite criar novos usuários' 
      },
      { 
        id: 'user_edit', 
        name: 'Editar Usuários', 
        description: 'Permite editar usuários existentes',
        resource: 'user',
        action: 'edit',
        nome: 'Editar Usuários', 
        descricao: 'Permite editar usuários existentes' 
      },
      { 
        id: 'user_delete', 
        name: 'Deletar Usuários', 
        description: 'Permite deletar usuários',
        resource: 'user',
        action: 'delete',
        nome: 'Deletar Usuários', 
        descricao: 'Permite deletar usuários' 
      },
      
      // Vistorias
      { 
        id: 'vistoria_view', 
        name: 'Visualizar Vistorias', 
        description: 'Permite visualizar vistorias',
        resource: 'vistoria',
        action: 'view',
        nome: 'Visualizar Vistorias', 
        descricao: 'Permite visualizar vistorias' 
      },
      { 
        id: 'vistoria_create', 
        name: 'Criar Vistorias', 
        description: 'Permite criar novas vistorias',
        resource: 'vistoria',
        action: 'create',
        nome: 'Criar Vistorias', 
        descricao: 'Permite criar novas vistorias' 
      },
      { 
        id: 'vistoria_edit', 
        name: 'Editar Vistorias', 
        description: 'Permite editar vistorias existentes',
        resource: 'vistoria',
        action: 'edit',
        nome: 'Editar Vistorias', 
        descricao: 'Permite editar vistorias existentes' 
      },
      { 
        id: 'vistoria_delete', 
        name: 'Deletar Vistorias', 
        description: 'Permite deletar vistorias',
        resource: 'vistoria',
        action: 'delete',
        nome: 'Deletar Vistorias', 
        descricao: 'Permite deletar vistorias' 
      },
      
      // Calendário
      { 
        id: 'calendario_view', 
        name: 'Visualizar Calendário', 
        description: 'Permite visualizar calendários',
        resource: 'calendario',
        action: 'view',
        nome: 'Visualizar Calendário', 
        descricao: 'Permite visualizar calendários' 
      },
      { 
        id: 'calendario_create', 
        name: 'Criar Calendário', 
        description: 'Permite criar novos calendários',
        resource: 'calendario',
        action: 'create',
        nome: 'Criar Calendário', 
        descricao: 'Permite criar novos calendários' 
      },
      { 
        id: 'calendario_edit', 
        name: 'Editar Calendário', 
        description: 'Permite editar calendários existentes',
        resource: 'calendario',
        action: 'edit',
        nome: 'Editar Calendário', 
        descricao: 'Permite editar calendários existentes' 
      },
      { 
        id: 'calendario_delete', 
        name: 'Deletar Calendário', 
        description: 'Permite deletar calendários',
        resource: 'calendario',
        action: 'delete',
        nome: 'Deletar Calendário', 
        descricao: 'Permite deletar calendários' 
      },
      
      // Cardápio
      { 
        id: 'cardapio_view', 
        name: 'Visualizar Cardápio', 
        description: 'Permite visualizar cardápios',
        resource: 'cardapio',
        action: 'view',
        nome: 'Visualizar Cardápio', 
        descricao: 'Permite visualizar cardápios' 
      },
      { 
        id: 'cardapio_create', 
        name: 'Criar Cardápio', 
        description: 'Permite criar novos cardápios',
        resource: 'cardapio',
        action: 'create',
        nome: 'Criar Cardápio', 
        descricao: 'Permite criar novos cardápios' 
      },
      { 
        id: 'cardapio_edit', 
        name: 'Editar Cardápio', 
        description: 'Permite editar cardápios existentes',
        resource: 'cardapio',
        action: 'edit',
        nome: 'Editar Cardápio', 
        descricao: 'Permite editar cardápios existentes' 
      },
      { 
        id: 'cardapio_delete', 
        name: 'Deletar Cardápio', 
        description: 'Permite deletar cardápios',
        resource: 'cardapio',
        action: 'delete',
        nome: 'Deletar Cardápio', 
        descricao: 'Permite deletar cardápios' 
      },
      
      // Geolocalização
      { 
        id: 'geolocation_view', 
        name: 'Visualizar Geolocalização', 
        description: 'Permite visualizar dados de geolocalização',
        resource: 'geolocation',
        action: 'view',
        nome: 'Visualizar Geolocalização', 
        descricao: 'Permite visualizar dados de geolocalização' 
      },
      { 
        id: 'geolocation_edit', 
        name: 'Editar Geolocalização', 
        description: 'Permite editar dados de geolocalização',
        resource: 'geolocation',
        action: 'edit',
        nome: 'Editar Geolocalização', 
        descricao: 'Permite editar dados de geolocalização' 
      },
      
      // Parâmetros
      { 
        id: 'parametro_view', 
        name: 'Visualizar Parâmetros', 
        description: 'Permite visualizar parâmetros do sistema',
        resource: 'parametro',
        action: 'view',
        nome: 'Visualizar Parâmetros', 
        descricao: 'Permite visualizar parâmetros do sistema' 
      },
      { 
        id: 'parametro_edit', 
        name: 'Editar Parâmetros', 
        description: 'Permite editar parâmetros do sistema',
        resource: 'parametro',
        action: 'edit',
        nome: 'Editar Parâmetros', 
        descricao: 'Permite editar parâmetros do sistema' 
      },
      
      // Relatórios
      { 
        id: 'relatorio_view', 
        name: 'Visualizar Relatórios', 
        description: 'Permite visualizar relatórios',
        resource: 'relatorio',
        action: 'view',
        nome: 'Visualizar Relatórios', 
        descricao: 'Permite visualizar relatórios' 
      },
      { 
        id: 'relatorio_export', 
        name: 'Exportar Relatórios', 
        description: 'Permite exportar relatórios',
        resource: 'relatorio',
        action: 'export',
        nome: 'Exportar Relatórios', 
        descricao: 'Permite exportar relatórios' 
      },
      
      // Ajuda
      { 
        id: 'help_view', 
        name: 'Visualizar Ajuda', 
        description: 'Permite acessar a seção de ajuda',
        resource: 'help',
        action: 'view',
        nome: 'Visualizar Ajuda', 
        descricao: 'Permite acessar a seção de ajuda' 
      },
      
      // Sistema
      { 
        id: 'system_config', 
        name: 'Configurar Sistema', 
        description: 'Permite configurar o sistema',
        resource: 'system',
        action: 'config',
        nome: 'Configurar Sistema', 
        descricao: 'Permite configurar o sistema' 
      },
      { 
        id: 'system_backup', 
        name: 'Backup do Sistema', 
        description: 'Permite fazer backup do sistema',
        resource: 'system',
        action: 'backup',
        nome: 'Backup do Sistema', 
        descricao: 'Permite fazer backup do sistema' 
      }
    ]
  }
}
